const BRANCH_SCRIPT = "https://cdn.branch.io/branch-latest.min.js";
const BRANCH_CONSENT_GROUP = "C0004";
const BRANCH_METHODS =
  "addListener banner closeBanner closeJourney data deepview deepviewCta first init link logout removeListener setBranchViewData setIdentity track trackCommerceEvent logEvent disableTracking getBrowserFingerprintId crossPlatformIds lastAttributedTouchData setAPIResponseCallback qrCode setRequestMetaData setAPIUrl getAPIUrl setDMAParamsForEEA".split(
    " ",
  );

interface BranchInitOptions {
  no_journeys?: boolean;
  tracking_disabled?: boolean;
}

interface BranchStub extends BranchWebSdk {
  _q: unknown[];
  _v: number;
}

declare global {
  interface Window {
    OnetrustActiveGroups?: string;
    OptanonWrapper?: () => void;
  }
}

function installBranchStub(): BranchStub {
  const existing = window.branch as BranchStub | undefined;
  if (existing?._q) return existing;

  const branch = { _q: [] as unknown[], _v: 1 } as BranchStub;
  for (const method of BRANCH_METHODS) {
    (branch as unknown as Record<string, unknown>)[method] = function (
      this: BranchStub,
      ...args: unknown[]
    ) {
      this._q.push([method, args]);
    };
  }
  window.branch = branch;
  return branch;
}

function hasBranchConsent(): boolean {
  const match = document.cookie.match(/(?:^|; )OptanonConsent=([^;]*)/);
  if (!match) return false;
  const groups =
    new URLSearchParams(decodeURIComponent(match[1])).get("groups") || "";
  return groups.split(",").indexOf(`${BRANCH_CONSENT_GROUP}:1`) !== -1;
}

export const initializeBranch = (branchKey: string): void => {
  if (!branchKey) return;

  // If the Branch script is already in the DOM, return and do nothing.
  if (document.querySelector(`script[src="${BRANCH_SCRIPT}"]`)) return;

  const branch = installBranchStub();

  const script = document.createElement("script");
  script.async = true;
  script.src = BRANCH_SCRIPT;
  document.head.appendChild(script);

  // The SDK is still downloading, so read the saved consent cookie and leave
  // tracking off unless targeting cookies (C0004) were accepted.
  let branchConsent = hasBranchConsent();
  const options: BranchInitOptions = {
    tracking_disabled: !branchConsent,
  };
  // Journeys prompt the visitor to open the app, so skip them inside the app webview.
  if (new URLSearchParams(window.location.search).has("webview")) {
    options.no_journeys = true;
  }

  branch.init(branchKey, options);

  // If the visitor accepts or rejects later, update Branch to match.
  // Only do that when the answer changes, because turning tracking back on restarts Branch.
  window.OptanonWrapper = () => {
    const groups = window.OnetrustActiveGroups || "";
    const allowed = `,${groups},`.indexOf(`,${BRANCH_CONSENT_GROUP},`) !== -1;
    if (allowed === branchConsent) return;
    branchConsent = allowed;
    // Look window.branch up at call time. The real SDK replaces the stub, and a
    // saved reference would keep writing to the old queue.
    window.branch?.disableTracking(!allowed);
  };
};
